import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '../../utils/cn'
import { useFocusTrap, useScrollLock } from './useFocusTrap'
import { Button } from './Button'

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

const SIZE: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[calc(100vw-2rem)]',
}

export interface DialogProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  size?: DialogSize
  /** Close when the backdrop is clicked (default true). */
  dismissable?: boolean
  /** Hide the × button. */
  hideClose?: boolean
  className?: string
  bodyClassName?: string
  /** z-index class — bump when stacking over a Drawer. */
  zIndexClassName?: string
  role?: 'dialog' | 'alertdialog'
}

/**
 * Dialog — centred modal with role="dialog", aria-modal, focus trap, Escape
 * to close, focus restore, scroll lock, portal to document.body.
 */
export function Dialog({
  open, onClose, title, description, children, footer, size = 'md',
  dismissable = true, hideClose, className, bodyClassName, zIndexClassName = 'z-[70]', role = 'dialog',
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descId = useId()
  const handleEscape = useCallback(() => { if (dismissable) onClose() }, [dismissable, onClose])
  useFocusTrap(panelRef, open, { onEscape: handleEscape })
  useScrollLock(open)

  // Guard against SSR / tests without document.
  useEffect(() => { /* keep hook order stable */ }, [])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div className={cn('fixed inset-0 flex items-center justify-center p-4', zIndexClassName)}>
      <div className="absolute inset-0 bg-black/40 animate-fade-in" onClick={dismissable ? onClose : undefined} aria-hidden />
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cn(
          'relative w-full bg-surface rounded-xl shadow-2xl flex flex-col max-h-[calc(100vh-2rem)] animate-fade-in outline-none',
          SIZE[size], className,
        )}
      >
        {(title || !hideClose) && (
          <div className="flex items-start gap-3 px-5 pt-4 pb-3 border-b border-line">
            <div className="min-w-0 flex-1">
              {title && <h2 id={titleId} className="text-base font-semibold text-fg leading-6">{title}</h2>}
              {description && <p id={descId} className="text-sm text-fg-muted mt-0.5">{description}</p>}
            </div>
            {!hideClose && (
              <Button variant="ghost" size="sm" iconOnly aria-label="Close" onClick={onClose} className="-mr-1 -mt-1">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
        <div className={cn('px-5 py-4 overflow-y-auto flex-1 min-h-0 text-sm text-fg', bodyClassName)}>{children}</div>
        {footer && <div className="px-5 py-3 border-t border-line flex items-center justify-end gap-2 bg-surface-sunken/60 rounded-b-xl">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}

export default Dialog
